(ns io.daskul.webapp.api.http
  "Shared imperative HTTP helper for component callbacks.

   The third frontend HTTP idiom (see ADR F-054):

   - build-request -> re-frame :http-xhrio effect map (for reg-event-fx)
   - use-fetch     -> declarative read hook (mount/deps-driven)
   - request!      -> imperative one-shot for clicks/writes (THIS NS)

   request! is a thin ajax.core wrapper that reads :api-base / :auth-header /
   :on-http-error from the shared config (populated by webapp/configure!). It
   reuses api.core/base-opts (URI + headers) and routes failures through
   api.core/handle-http-error, so a 401 fires the configured unauthorized/logout
   flow exactly like the re-frame path — the correctness win this helper exists
   for.

   Usage:
     (request! {:method :post
                :path \"/sessions\"
                :params {...}
                :on-success (fn [resp] ...)
                :on-error   (fn [err] ...)})"
  (:require
   [re-frame.core :as rf]
   [ajax.core :as ajax]
   [io.daskul.webapp.api.core :as api.core]))

(def ^:private method->fn
  {:get    ajax/GET
   :post   ajax/POST
   :put    ajax/PUT
   :patch  ajax/PATCH
   :delete ajax/DELETE})

(defn- run-http-error-policy!
  "Apply the shared 401/error policy. handle-http-error returns an effect map
   (e.g. {:dispatch [...]}) or nil; when present, run its :dispatch so the
   configured unauthorized flow fires from an imperative call too."
  [error]
  (when-let [effect (api.core/handle-http-error error)]
    (when-let [event (:dispatch effect)]
      (rf/dispatch event))))

(defn request!
  "Imperative one-shot API call from a component callback (click / write /
   ad-hoc read). Reads base URL + auth header + 401 policy from shared config.

   Options:
   - :method     - :get | :post | :put | :patch | :delete (default :get)
   - :path       - URL path relative to API base (e.g. \"/sessions\")
   - :params     - request params (query for GET, JSON body for writes)
   - :on-success - (fn [response]) called with the parsed (keywordized) body
   - :on-error   - (fn [error]) called after the shared 401/error policy runs

   On failure the shared handle-http-error policy runs FIRST (so a 401 triggers
   the configured logout), THEN :on-error is invoked for caller-local handling."
  [{:keys [method path params on-success on-error]
    :or   {method :get}}]
  (let [http-fn (get method->fn method)
        {:keys [uri headers]} (api.core/base-opts path)]
    (when-not http-fn
      (throw (js/Error. (str "Unsupported HTTP method: " method))))
    (http-fn uri
             {:params params
              :headers headers
              :format (ajax/json-request-format)
              :response-format (ajax/json-response-format {:keywords? true})
              :handler (fn [response]
                         (when on-success (on-success response)))
              :error-handler (fn [error]
                               (run-http-error-policy! error)
                               (when on-error (on-error error)))})))
